import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Package } from "lucide-react";

export interface AddonSelection {
  addon_id: string;
  name: string;
  quantity: number;
  price: number; // öre
}

interface BookingAddonsStepProps {
  organizationId: string;
  selections: AddonSelection[];
  onSelectionsChange: (selections: AddonSelection[]) => void;
  bookingStartTime?: string; // "14:30"
  bookingDayOfWeek?: number; // 1-7
}

export default function BookingAddonsStep({
  organizationId,
  selections,
  onSelectionsChange,
  bookingStartTime,
  bookingDayOfWeek,
}: BookingAddonsStepProps) {
  const { data: addons = [] } = useQuery({
    queryKey: ["booking-addons", organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_addons" as any)
        .select("id, name, description, price, sort_order, available_from, available_to, available_days")
        .eq("organization_id", organizationId)
        .eq("is_active", true)
        .order("sort_order");
      return (data as any[]) ?? [];
    },
    enabled: !!organizationId,
  });

  const availableAddons = addons.filter((addon: any) => {
    if (addon.available_from && addon.available_to && bookingStartTime) {
      if (bookingStartTime < addon.available_from.slice(0, 5) ||
          bookingStartTime >= addon.available_to.slice(0, 5)) {
        return false;
      }
    }
    if (addon.available_days && bookingDayOfWeek) {
      if (!addon.available_days.includes(bookingDayOfWeek)) {
        return false;
      }
    }
    return true;
  });

  if (availableAddons.length === 0) return null;

  const toggle = (addon: any) => {
    const exists = selections.find((s) => s.addon_id === addon.id);
    if (exists) {
      onSelectionsChange(selections.filter((s) => s.addon_id !== addon.id));
    } else {
      onSelectionsChange([...selections, { addon_id: addon.id, name: addon.name, quantity: 1, price: addon.price }]);
    }
  };

  const updateQty = (addonId: string, delta: number) => {
    onSelectionsChange(
      selections.map((s) =>
        s.addon_id === addonId ? { ...s, quantity: Math.max(1, s.quantity + delta) } : s
      )
    );
  };

  return (
    <div>
      <Label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1.5">
        <Package className="h-3.5 w-3.5" /> Tillägg
      </Label>
      <div className="space-y-2">
        {availableAddons.map((addon: any) => {
          const sel = selections.find((s) => s.addon_id === addon.id);
          const isSelected = !!sel;
          return (
            <div
              key={addon.id}
              className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              }`}
              onClick={() => toggle(addon)} /* G-107: card click toggles both ways */
            >
              <div className="flex items-start gap-3">
                {/* G-107: display-only — the card onClick is the single toggle
                    source, so the checkbox must not also fire (double-toggle). */}
                <Checkbox
                  checked={isSelected}
                  className="mt-0.5 pointer-events-none"
                  tabIndex={-1}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-medium">{addon.name}</span>
                    <span className="text-sm font-semibold text-foreground ml-2">{addon.price} kr</span>
                  </div>
                  {addon.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{addon.description}</p>
                  )}
                </div>
              </div>
              {isSelected && (
                <div className="flex items-center gap-2 mt-2 ml-7">
                  <span className="text-xs text-muted-foreground">Antal:</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); updateQty(addon.id, -1); }}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="text-sm font-medium w-6 text-center">{sel!.quantity}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); updateQty(addon.id, 1); }}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {sel!.quantity * addon.price} kr
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
